import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Truck, Building, Phone, Mail, MapPin, Globe, Search } from 'lucide-react';
import SupplierForm from '../components/SupplierForm'; // Importa el nuevo componente
import { suppliersService } from '../services/suppliersService'; // Importa el servicio

const SuppliersPage = ({ suppliers, setSuppliers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localSuppliers, setLocalSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  // Manejar el estado de navegación para abrir el formulario de edición
  useEffect(() => {
    if (location.state?.editSupplier) {
      setEditingSupplier(location.state.editSupplier);
      setShowForm(true);
      // Limpiar el estado de navegación
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Cargar proveedores siempre al montar el componente
  useEffect(() => {
    console.log('🚀 SuppliersPage useEffect ejecutándose...');
    
    const loadSuppliers = async () => {
      try {
        setLoading(true);
        console.log('🔄 Iniciando carga de proveedores...');
        
        const response = await suppliersService.getAll();
        console.log('📊 Respuesta proveedores:', response);
        
        // Extraer los datos según el formato de respuesta
        let data = [];
        if (response.success && response.data) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response.data && Array.isArray(response.data)) {
          data = response.data;
        }
        
        console.log('✅ Proveedores procesados:', data.length, data);
        setLocalSuppliers(data);
        
        if (setSuppliers) {
          setSuppliers(data);
        }
      } catch (error) {
        console.error('❌ Error cargando proveedores:', error);
        console.error('❌ Stack trace:', error.stack);
      } finally {
        setLoading(false);
      }
    };

    loadSuppliers();
  }, []); // Dependencias vacías para cargar siempre

  // Filtrar proveedores basado en el término de búsqueda
  useEffect(() => {
    const suppliersToFilter = suppliers && suppliers.length > 0 ? suppliers : localSuppliers;
    
    if (!searchTerm.trim()) {
      setFilteredSuppliers(suppliersToFilter);
    } else {
      const filtered = suppliersToFilter.filter(supplier =>
        supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [suppliers, localSuppliers, searchTerm]);

  // Usar proveedores filtrados
  const suppliersToShow = filteredSuppliers;

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDeleteSupplier = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este proveedor? ¡No hay vuelta atrás!')) {
      try {
        setLoading(true);
        await suppliersService.delete(id);
        
        // Actualizar tanto props como estado local
        if (setSuppliers) {
          setSuppliers(prevSuppliers => prevSuppliers.filter(s => s.id !== id));
        }
        setLocalSuppliers(prevSuppliers => prevSuppliers.filter(s => s.id !== id));
        
        console.log('✅ Proveedor eliminado exitosamente');
      } catch (error) {
        console.error('❌ Error eliminando proveedor:', error);
        alert('Error eliminando el proveedor: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveSupplier = async (supplierData) => {
    try {
      setLoading(true);
      let result;
      
      if (supplierData.id) {
        // Editar proveedor existente
        result = await suppliersService.update(supplierData.id, supplierData);
        
        // Actualizar tanto props como estado local
        if (setSuppliers) {
          setSuppliers(prevSuppliers =>
            prevSuppliers.map(s => (s.id === supplierData.id ? result.data : s))
          );
        }
        setLocalSuppliers(prevSuppliers =>
          prevSuppliers.map(s => (s.id === supplierData.id ? result.data : s))
        );
        
        console.log('✅ Proveedor actualizado exitosamente');
      } else {
        // Añadir nuevo proveedor
        result = await suppliersService.create(supplierData);
        
        // Actualizar tanto props como estado local
        if (setSuppliers) {
          setSuppliers(prevSuppliers => [...prevSuppliers, result.data]);
        }
        setLocalSuppliers(prevSuppliers => [...prevSuppliers, result.data]);
        
        console.log('✅ Proveedor creado exitosamente');
      }
      
      setShowForm(false);
      setEditingSupplier(null);
    } catch (error) {
      console.error('❌ Error guardando proveedor:', error);
      alert('Error guardando el proveedor: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingSupplier(null);
  };

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
        Gestión de Proveedores
      </h2>

      {/* Barra de búsqueda */}
      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nombre, contacto, email, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all duration-300 text-gray-700 placeholder-gray-400"
          />
        </div>
      </div>

      <div className="flex justify-center gap-4 mb-8">
        <motion.button
          onClick={handleAddSupplier}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <PlusCircle className="w-5 h-5" />
          Nuevo Proveedor
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {showForm && (
          <SupplierForm
            currentSupplier={editingSupplier}
            onSave={handleSaveSupplier}
            onCancel={handleCancelForm}
          />
        )}
      </AnimatePresence>

      {!showForm && (
        <motion.div
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-5xl mx-auto border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {suppliersToShow.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Truck className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">
                {loading ? 'Cargando proveedores...' : 'No hay proveedores registrados. ¡Añade uno!'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <AnimatePresence>
                    {suppliersToShow.map((supplier) => (
                      <motion.tr
                        key={supplier.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/suppliers/supplier/${supplier.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="w-5 h-5 text-gray-500 mr-2" />
                            <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{supplier.contactPerson}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 text-gray-400 mr-1" />
                            {supplier.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 text-gray-400 mr-1" />
                            {supplier.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSupplier(supplier);
                              }}
                              className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Edit className="w-4 h-4" />
                              Editar
                            </motion.button>
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSupplier(supplier.id);
                              }}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Trash2 className="w-4 h-4" />
                              Borrar
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default SuppliersPage;